// Price check — the ONE place that decides how a listing's own channel price
// compares with what the competitive checker found on that marketplace.
// Every surface (listing card chips, the panel, the edit-page bar, the Overview
// list) reads this module and never re-derives a state on its own.
//
// Units (the trap here):
//   - `{mp}_price` is the listing's OWN channel price, stored in PENCE like
//     every money column on the listing (the form divides by 100 on load and
//     multiplies on submit — helpers/Form.js, helpers/Listing.js).
//   - The checker's figures (`{mp}_lowest_prices`, `_median_price`,
//     `_avg5low_price`, `_low_price`) are written by the external checker in
//     MAJOR units — the old PurpleOrb printed them raw with a £ in front.
// Everything returned from here is in major units.

export const PRICE_CHECK_MARKETPLACES = ["chrono", "ebay", "wf"];

export const MARKETPLACE_LABELS = {
  chrono: "Chrono24",
  ebay: "eBay",
  wf: "WatchFinder",
};

export const REVIEW_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

// States that put a listing on the review list.
export const NEEDS_REVIEW_STATES = ["above", "below"];
export const needsReview = (state) => NEEDS_REVIEW_STATES.includes(state);

// The state → tone mapping the UI colours by (success / danger / warning /
// neutral match the app's --status-* tokens).
export const STATE_TONE = {
  above: "danger",
  // Both directions are out of range and need a look: red, like above.
  below: "danger",
  in_range: "success",
  no_data: "neutral",
  reviewed: "neutral",
  unchecked: "neutral",
};

const parseJsonish = (raw) => {
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  }
  return raw;
};

const toNumber = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// `{mp}_date` arrives either as an ISO/SQL datetime or, from the older checker,
// as "dd-mm-yyyy HH:MM:SS" (which `new Date` cannot read).
export const parseCheckDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const str = String(value);
  const dmY = /^(\d{2})-(\d{2})-(\d{4})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(str);
  if (dmY) {
    const [, d, m, y, hh, mm, ss = "00"] = dmY;
    const date = new Date(`${y}-${m}-${d}T${hh}:${mm}:${ss}`);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(str.includes(" ") && !str.includes("T") ? str.replace(" ", "T") : str);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const parseSkipCompCheck = (listing) => {
  const parsed = parseJsonish(listing?.skip_comp_check);
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
};

const parseSkipTimestamps = (listing) => {
  const parsed = parseJsonish(listing?.skip_comp_check_timestamps);
  return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
};

// Sorted, numeric lowest prices (max 5) in major units — mirrors the backend's
// CompetitivePricing::lowestPrices() so Overview counts and rows agree.
export const sortedLowestPrices = (listing, mp) => {
  const raw = parseJsonish(listing?.[`${mp}_lowest_prices`]);
  if (!Array.isArray(raw)) return [];
  return raw
    .map(toNumber)
    .filter((n) => n !== null)
    .sort((a, b) => a - b)
    .slice(0, 5);
};

// What the checker saw on each of the cheapest Chrono24 results — year and
// stock — keyed to the sorted prices. `chrono_lowest_details` is
// [{price, year, in_stock}] in the same units as chrono_lowest_prices; older
// checks have none, so every row then carries nulls and the panel shows the
// price alone.
const lowestRows = (listing, mp, lowestList) => {
  const raw = mp === "chrono" ? parseJsonish(listing?.chrono_lowest_details) : null;
  const pool = (Array.isArray(raw) ? raw : [])
    .filter((d) => d && typeof d === "object" && toNumber(d.price) !== null)
    .map((d) => {
      const availability = d.availability ? String(d.availability) : null;
      const inStock = d.in_stock === null || d.in_stock === undefined
        ? (availability ? /in stock/i.test(availability) : null)
        : !!d.in_stock;
      return {
        price: toNumber(d.price),
        year: toNumber(d.year),
        inStock,
        availability,
        link: d.link || null,
        country: d.country || null,
        // What the seller asked in their own currency (Chrono24 converts it
        // with its own rate for other sites) — shown when it differs.
        originalPrice: toNumber(d.original_price),
        originalCurrency: d.original_currency ? String(d.original_currency).toLowerCase() : null,
      };
    });
  const blank = { year: null, inStock: null, availability: null, link: null, country: null, originalPrice: null, originalCurrency: null };
  return lowestList.map((price) => {
    const i = pool.findIndex((d) => Math.abs(d.price - price) < 1);
    const hit = i === -1 ? null : pool.splice(i, 1)[0];
    return { ...blank, ...(hit || {}), price };
  });
};

// The listing's own channel price in major units (null when unset). 0 (Chrono24
// "Price on Request") and 1 (the eBay can't-be-£0 placeholder) are sentinels,
// not prices — same rule as the backend's App\Support\CompetitivePricing.
export const ownChannelPrice = (listing, mp) => {
  const pence = toNumber(listing?.[`${mp}_price`]);
  return pence === null || pence <= 1 ? null : pence / 100;
};

/**
 * @param {object} listing
 * @param {"chrono"|"ebay"|"wf"} mp
 * @param {{ now?: Date|number, priceOverride?: number|null }} [opts]
 *   priceOverride — a MAJOR-unit price to judge instead of the stored one
 *   (the edit page passes the unsaved form value so the verdict is live).
 */
export const getPriceCheck = (listing, mp, opts = {}) => {
  const now = opts.now instanceof Date ? opts.now.getTime() : Number(opts.now ?? Date.now());
  const hasOverride = Object.prototype.hasOwnProperty.call(opts, "priceOverride");
  const override = hasOverride ? toNumber(opts.priceOverride) : null;
  const price = hasOverride ? (override !== null && override > 0 ? override : null) : ownChannelPrice(listing, mp);

  const lowestList = sortedLowestPrices(listing, mp);
  const checkedAt = parseCheckDate(listing?.[`${mp}_date`]);
  const count = toNumber(listing?.[`${mp}_listing_count`]);

  const base = {
    marketplace: mp,
    label: MARKETPLACE_LABELS[mp] || mp,
    price,
    lowestList,
    rows: lowestRows(listing, mp, lowestList),
    lowest: lowestList.length ? lowestList[0] : null,
    fifth: lowestList.length ? lowestList[Math.min(4, lowestList.length - 1)] : null,
    topCount: Math.min(5, lowestList.length),
    median: toNumber(listing?.[`${mp}_median_price`]),
    avg5: toNumber(listing?.[`${mp}_avg5low_price`]),
    count,
    checkedAt,
    url: listing?.[`${mp}_url`] || null,
    gap: null,
    suggestedPrice: null,
    rank: null,
    reviewedUntil: null,
  };

  if (!checkedAt) return { ...base, state: "unchecked" };

  const skip = parseSkipCompCheck(listing);
  if (skip[mp] === true || skip[mp] === 1 || skip[mp] === "1") {
    const ts = parseCheckDate(parseSkipTimestamps(listing)[mp]);
    if (ts) {
      const until = new Date(ts.getTime() + REVIEW_DAYS * DAY_MS);
      if (until.getTime() > now) return { ...base, state: "reviewed", reviewedUntil: until };
    }
  }

  if (lowestList.length < 2 || price === null) return { ...base, state: "no_data" };

  if (price > base.fifth) {
    return { ...base, state: "above", gap: price - base.fifth, suggestedPrice: base.fifth };
  }
  if (price < base.lowest) {
    return { ...base, state: "below", gap: base.lowest - price, suggestedPrice: base.lowest };
  }
  // In range: 1-based position among the cheapest listings (ties share a place).
  const rank = lowestList.filter((p) => p < price).length + 1;
  return { ...base, state: "in_range", rank };
};

// ── Other Chrono24 accounts (CH24 Europe, Dubai…) ───────────────────────────
// The nightly check runs ONCE per listing, on Chrono24 in GBP. Every other
// Chrono24 account is judged against that same search: its market prices are
// converted into the account's currency and rounded UP to the next 50 (so they
// end in 50 or 00), and the account's own price is compared with those. The
// suggested price is therefore a round figure inside the converted top 5.
export const roundUpTo50 = (n) => Math.ceil(n / 50) * 50;

/**
 * @param {object} listing
 * @param {{id:number,label:string,currency:string}} account  a non-legacy Chrono24 account
 * @param {{ price: number|null, rate: number|null, now?: Date|number }} opts
 *   price — the account's own price in MAJOR units of its currency;
 *   rate  — value of 1 unit of the account currency in GBP (useAllFx("GBP")).
 */
export const getAccountPriceCheck = (listing, account, { price = null, rate = null, now, market = null } = {}) => {
  const currency = String(account?.currency || "GBP").toUpperCase();
  // The checker's own search in this account's currency (chrono_markets → the
  // account's listing_marketplace_state row): prices exactly as that site
  // shows them, so no conversion of ours is involved.
  const real = parseJsonish(market?.lowest_prices);
  if (market?.comp_checked_at && Array.isArray(real) && real.some((p) => toNumber(p) !== null)) {
    const view = {
      ...listing,
      chrono_lowest_prices: real,
      chrono_lowest_details: market.lowest_details ?? null,
      chrono_median_price: market.median_price,
      chrono_avg5low_price: market.avg5low_price,
      chrono_listing_count: market.listing_count,
      chrono_url: market.comp_url || listing?.chrono_url,
      chrono_date: market.comp_checked_at,
    };
    return {
      ...getPriceCheck(view, "chrono", { now, priceOverride: price }),
      key: `chrono-${account?.id}`,
      accountId: account?.id ?? null,
      label: account?.label || "Chrono24",
      currency: currency.toLowerCase(),
      fx: currency === "GBP" ? 1 : toNumber(rate),
      real: true,
    };
  }
  const fx = currency === "GBP" ? 1 : toNumber(rate);
  const key = `chrono-${account?.id}`;
  const meta = { key, accountId: account?.id ?? null, label: account?.label || "Chrono24", currency: currency.toLowerCase(), fx };
  if (!fx || fx <= 0) {
    // No usable rate: never guess a conversion.
    return { ...getPriceCheck({}, "chrono"), ...meta, state: "unchecked", fxMissing: true };
  }
  // A GBP account needs no conversion, so its prices are left exactly as found.
  const conv = (v) => (toNumber(v) === null ? null : fx === 1 ? toNumber(v) : roundUpTo50(toNumber(v) / fx));
  const lowest = sortedLowestPrices(listing, "chrono").map(conv);
  const details = parseJsonish(listing?.chrono_lowest_details);
  const view = {
    ...listing,
    chrono_lowest_prices: lowest,
    chrono_lowest_details: Array.isArray(details)
      ? details.map((d) => (d && typeof d === "object" ? { ...d, price: conv(d.price) } : d))
      : null,
    chrono_median_price: conv(listing?.chrono_median_price),
    chrono_avg5low_price: conv(listing?.chrono_avg5low_price),
  };
  return { ...getPriceCheck(view, "chrono", { now, priceOverride: price }), ...meta };
};

// ── Comp Check Rule ─────────────────────────────────────────────────────────
// The search the checker ran IS its `{mp}_url` (sorted cheapest first), so the
// filters are read straight off that URL — exactly what was used, never a copy
// of the checker's logic that could drift.
const RULE_LABELS = {
  query: "Keywords",
  year: "Year",
  dialColor: "Dial colour (Chrono24 code)",
  countryIds: "Seller countries",
  condition: "Condition",
  gender: "Gender",
  SETCURR: "Currency",
  currencyId: "Currency",
  _nkw: "Keywords",
  _udlo: "Min price",
  _udhi: "Max price",
  maxAgeInDays: "Listed within (days)",
};
const RULE_SKIP = new Set(["dosearch", "sortorder", "pageSize", "showpage", "_sop", "_ipg", "redirectToSearchIndex", "resultview"]);

/** [[label, value], …] for the search behind a check, or [] when there is no usable URL. */
export const searchRule = (url) => {
  let parsed;
  try {
    parsed = new URL(url);
  } catch (_) {
    return [];
  }
  const out = [["Site", parsed.hostname.replace(/^www\./, "")]];
  const keys = [...new Set([...parsed.searchParams.keys()])];
  keys.forEach((k) => {
    if (RULE_SKIP.has(k)) return;
    const values = parsed.searchParams.getAll(k).filter(Boolean);
    if (!values.length) return;
    // Chrono24's "listed within N days"; 0 = no age limit, so not a filter.
    if (k === "maxAgeInDays" && values.every((v) => v === "0")) return;
    const value = k === "countryIds"
      ? `${values.length} (${values.join(", ")})`
      : values.join(", ");
    out.push([RULE_LABELS[k] || k, value]);
  });
  const sort = parsed.searchParams.get("sortorder") ?? parsed.searchParams.get("_sop");
  if (sort) out.push(["Sorted", "Price, lowest first"]);
  return out;
};

// ── Trade median (backend price_check_context, whole GBP) ──────────────────
export const TRADE_WINDOWS = [
  ["1m", "1M"],
  ["3m", "3M"],
  ["12m", "12M"],
  ["all", "All time"],
];

/** First window with a median, in the given order → { key, label, median } (GBP). */
export const tradeReference = (context, order = ["1m", "3m", "12m", "all"]) => {
  const medians = context?.trade_median || {};
  const key = order.find((k) => toNumber(medians[k]) !== null);
  return key ? { key, label: TRADE_WINDOWS.find(([k]) => k === key)[1], median: toNumber(medians[key]) } : null;
};

/**
 * Own price vs the trade median: 1M, else 3M, else 12M, else all time.
 * fx — value of 1 unit of the check's currency in GBP (1 for GBP); the GBP
 * median is converted into the check's currency before comparing.
 */
export const tradeGap = (price, context, fx = 1) => {
  const ref = tradeReference(context);
  const rate = toNumber(fx);
  if (!ref || price === null || price === undefined || !rate) return null;
  const median = Math.round(ref.median / rate);
  return { ...ref, median, diff: price - median };
};

/**
 * Item 5 — the CH24 UK price against the 3M trade median (else 12M, else all
 * time) as an order margin, (price − median) / median, next to the brand's
 * PriceIQ order margin. low = below the brand margin.
 */
export const marginCheck = (priceGbp, context) => {
  const ref = tradeReference(context, ["3m", "12m", "all"]);
  const brandMargin = toNumber(context?.brand_order_margin);
  if (!ref || !ref.median || priceGbp === null || priceGbp === undefined || brandMargin === null) return null;
  const margin = (priceGbp - ref.median) / ref.median;
  return { window: ref.label, median: ref.median, margin, brandMargin, brand: context.brand, low: margin < brandMargin };
};

/**
 * The check ran (dated) and told us how many priced listings it found, but
 * there were too few to judge (0 or 1). Worth showing — "checked, nothing to
 * compare" — unlike an old check with no count, or no check at all.
 */
export const checkedTooFew = (check) =>
  check?.state === "no_data" && !!check.checkedAt && typeof check.count === "number" && check.count < 2;

export const getPriceChecks = (listing, opts) =>
  PRICE_CHECK_MARKETPLACES.map((mp) => getPriceCheck(listing, mp, opts));

// Marketplaces worth showing a chip for: anything the checker has looked at.
export const checkedMarketplaces = (listing, opts) =>
  getPriceChecks(listing, opts).filter((c) => c.state !== "unchecked");

// Overview worklist: one row per listing × marketplace that is above, below
// or currently reviewed.
export const buildReviewRows = (listings = [], opts) =>
  listings.flatMap((listing) =>
    PRICE_CHECK_MARKETPLACES.map((mp) => ({
      key: `${listing.id}-${mp}`,
      listing,
      mp,
      check: getPriceCheck(listing, mp, opts),
    })).filter((r) => ["above", "below", "reviewed"].includes(r.check.state))
  );

export const ordinal = (n) => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};
